// Audit-event client helper.
//
// Fyrefly's audit-trail strategy (set 2026-05-12 evening):
//   * All audit-relevant user actions emit an event via emitAuditEvent().
//   * The actual hash chaining + integrity check runs server-side in
//     the `app.append_audit_event` RPC; the client just calls it.
//   * Best-effort: a failed audit emit is logged to console.warn but
//     does NOT block the user's action. The reasoning: a temporarily
//     offline client shouldn't make the app unusable, and the events
//     we miss are mostly informational (viewed-X timestamps). The
//     SAFETY-CRITICAL events (FRAT signed, flight plan committed) are
//     ALREADY persisted on the underlying row even without an audit
//     event; the audit log adds a richer per-action timeline on top.
//
// Event taxonomy (kind values):
//   * flight.aff_event              — AFF-spec event logged (drop / fill /
//                                    WoW / gear / status / position) — see
//                                    aff_events table for the full taxonomy
//   * flight.briefing_generated    — pilot generated a weather briefing
//   * flight.briefing_viewed       — pilot opened a previously-saved
//                                    briefing snapshot
//   * flight.weather_viewed        — pilot looked at METAR/TAF/winds
//                                    in the live app (non-briefing path)
//   * flight.plan_modified         — flight route / cruise / etc. changed
//   * flight.status_change         — planned → active → completed
//   * flight.tfr_viewed            — pilot tapped a TFR / SUA
//   * frat.created                 — FRAT assessment created
//   * frat.signed                  — FRAT assessment signed off
//   * frat.superseded              — FRAT assessment revised
//   * incident.assigned_aircraft   — aircraft tied to an incident
//   * incident.assigned_crew       — crew member tied to an incident
//   * org.member_invited           — crew member invited to the org
//   * org.member_role_changed      — admin changed someone's RBAC role
//
// Keep the kind string lowercase, dot-separated, past-tense verb.

import { supabase } from './supabase';
import { useAccountContextStore } from '../stores/accountContextStore';

// Module-level in-flight guard. Prevents a React-19 StrictMode double-mount
// or a fast retry from inserting two audit rows for the same logical event.
// Key = kind + subjectId + ISO-minute so that genuinely distinct events
// (e.g. two status changes within the same minute but for different subjects)
// are NOT suppressed.
const _inFlight = new Set<string>();

function _auditKey(opts: AuditEmitOpts): string {
  const minute = new Date().toISOString().slice(0, 16); // 'YYYY-MM-DDTHH:MM'
  return `${opts.kind}|${opts.subjectId ?? ''}|${minute}`;
}

export type AuditKind =
  | 'flight.aff_event'
  | 'flight.briefing_generated'
  | 'flight.briefing_viewed'
  | 'flight.weather_viewed'
  | 'flight.plan_modified'
  | 'flight.status_change'
  | 'flight.tfr_viewed'
  | 'frat.created'
  | 'frat.signed'
  | 'frat.superseded'
  | 'incident.assigned_aircraft'
  | 'incident.assigned_crew'
  | 'org.member_invited'
  | 'org.member_role_changed';

export interface AuditEmitOpts {
  kind: AuditKind;
  /** What was acted upon. 'flight' | 'frat' | 'incident' | 'tfr' | ... */
  subjectKind?: string;
  /** Client id of the subject row. */
  subjectId?: string;
  /** Structured event-specific payload. Will be canonicalized + hashed
   *  on the server, so keep it deterministic — no Date objects, no
   *  Map/Set, no random numbers. */
  payload?: Record<string, unknown>;
  /** Optional org override. Defaults to the active org context. */
  orgId?: string | null;
}

interface AuditEmitResult {
  ok: boolean;
  id?: string;
  error?: string;
}

/**
 * Emit a single audit event. Returns immediately; the actual server
 * write happens fire-and-forget. Result is awaitable when callers
 * care about the outcome (e.g. tests).
 */
export async function emitAuditEvent(opts: AuditEmitOpts): Promise<AuditEmitResult> {
  if (!supabase) {
    return { ok: false, error: 'supabase not configured' };
  }

  // Dedup guard: if this exact event is already in-flight (same kind +
  // subject + minute) from a StrictMode double-mount or an accidental
  // concurrent call, drop the duplicate silently.
  const key = _auditKey(opts);
  if (_inFlight.has(key)) {
    return { ok: false, error: 'duplicate in-flight' };
  }
  _inFlight.add(key);

  // Default to the active org. Personal mode → null org_id (event still
  // recorded against the user's personal chain).
  let orgId = opts.orgId;
  if (orgId === undefined) {
    const ctx = useAccountContextStore.getState().ctx;
    orgId = ctx.kind === 'organizational' ? ctx.orgId : null;
  }

  try {
    const { data, error } = await supabase.rpc('append_audit_event', {
      p_kind:         opts.kind,
      p_subject_kind: opts.subjectKind ?? null,
      p_subject_id:   opts.subjectId   ?? null,
      p_payload:      opts.payload     ?? {},
      p_org_id:       orgId,
    });
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[audit] emit failed:', opts.kind, error.message);
      return { ok: false, error: error.message };
    }
    const row = (Array.isArray(data) ? data[0] : data) as { id?: string } | null;
    return { ok: true, id: row?.id };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[audit] emit threw:', opts.kind, e);
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  } finally {
    _inFlight.delete(key);
  }
}

/**
 * Fire-and-forget convenience. Use this when you don't want to await
 * (the common case — the user's action shouldn't block on the audit).
 */
export function trackAuditEvent(opts: AuditEmitOpts): void {
  void emitAuditEvent(opts);
}
